/* Shared HTML template for the fee-report PDF exports (Due Fees, Discounts,
   Transactions). Keeps the printed layout — blue title band, school/year/date
   strip, bordered table — identical across the three reports. Colors read
   from the same CSS custom properties as the on-screen UI (App.css); this
   resolves correctly because html2pdf.js appends its render container to
   document.body before rasterizing it. */

export const escapeHtml = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const BADGE_TONE_COLOR = {
  success: "var(--color-success)",
  error: "var(--color-error)",
  warning: "var(--color-primary)",
};

/* Colored status pill matching the on-screen Report.module.css badges
   (badgeApproved/badgePaid, badgeRejected, badgePending/badgePartlyPaid). */
export const pdfBadge = (text, tone = "warning") =>
  `<span style="display:inline-block;min-width:80px;padding:3px 10px;border-radius:4px;font-size:9px;font-weight:600;color:var(--color-white);background:${
    BADGE_TONE_COLOR[tone] || BADGE_TONE_COLOR.warning
  };text-align:center;">${escapeHtml(text)}</span>`;

/**
 * @param {string} title
 * @param {Array<string|false|null|undefined>} infoItems - rendered bullet-
 *   separated under the title; falsy entries are dropped.
 * @param {Array<{key:string,label:string,render?:(value:any,row:object)=>string}>} headers
 * @param {Array<object>} rows
 */
export const buildReportPdfHtml = ({ title, infoItems, headers, rows }) => {
  const infoHtml = infoItems
    .filter(Boolean)
    .map((item) => `<span>${escapeHtml(item)}</span>`)
    .join(`<span style="margin:0 12px;color:var(--color-blue);">&bull;</span>`);

  return `
    <div style="font-family:var(--font-family-primary, Arial, sans-serif);">
      <div style="background:var(--color-blue);padding:14px 12px;border-radius:6px 6px 0 0;">
        <h2 style="margin:0;font-size:17px;font-weight:700;letter-spacing:0.5px;color:var(--color-white);text-align:center;text-transform:uppercase;">
          ${escapeHtml(title)}
        </h2>
      </div>
      <div style="background:var(--color-report-demand-bg);border:1px solid var(--color-report-demand-border);border-top:none;padding:9px 12px;text-align:center;font-size:11px;font-weight:600;color:var(--color-blue);">
        ${infoHtml}
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:14px;">
        <thead>
          <tr>${headers
            .map(
              (h) =>
                `<th style="border:1px solid var(--color-neutral-350);padding:8px 6px;background:var(--color-neutral-150);color:var(--color-blue);font-weight:700;text-align:left;">${escapeHtml(
                  h.label,
                )}</th>`,
            )
            .join("")}</tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) =>
                `<tr>${headers
                  .map(
                    (h) =>
                      `<td style="border:1px solid var(--color-neutral-350);padding:6px;color:var(--color-text);">${
                        h.render ? h.render(r[h.key], r) : escapeHtml(r[h.key])
                      }</td>`,
                  )
                  .join("")}</tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>`;
};
